import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Bot,
  ChevronDown,
  ChevronRight,
  Loader2,
  Send,
  Square,
  Terminal,
  TriangleAlert,
  User,
  WifiOff,
} from 'lucide-react'
import clsx from 'clsx'
import aiEngineApi from '../../services/aiEngineApi'
import { useTenant } from '../../hooks/useTenant'
import { useAuth } from '../../contexts/AuthContext'
import ListaSessoes from './ListaSessoes'

const POLL_INTERVAL_MS = 900
// O turno roda no servidor. Uma falha de rede aqui não significa que ele morreu —
// significa que perdemos a visão dele. Continuamos tentando por ~1,5 min antes de
// desistir, e o histórico persistido recupera o resto quando a página reabrir.
const MAX_POLL_FAILURES = 100

/** Bloco de uso de ferramenta — colapsado por padrão; a saída crua costuma ser ruído. */
function ToolCall({ event }) {
  const [open, setOpen] = useState(false)
  const Chevron = open ? ChevronDown : ChevronRight
  return (
    <div className="my-1 rounded-theme-md border border-theme-border bg-theme-bg/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-mono text-theme-text-muted hover:text-theme-text"
      >
        <Chevron className="h-3.5 w-3.5 shrink-0" />
        <Terminal className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{event.name}</span>
      </button>
      {open && (
        <pre className="max-h-72 overflow-auto border-t border-theme-border px-3 py-2 text-[11px] leading-relaxed text-theme-text-muted whitespace-pre-wrap break-all">
          {typeof event.input === 'string' ? event.input : JSON.stringify(event.input, null, 2)}
        </pre>
      )}
    </div>
  )
}

function EventBlock({ event }) {
  if (event.type === 'text') {
    return <p className="whitespace-pre-wrap leading-relaxed text-theme-text">{event.text}</p>
  }
  if (event.type === 'tool_use') return <ToolCall event={event} />
  if (event.type === 'tool_result' && event.isError) {
    return (
      <p className="my-1 rounded-theme-md border border-error-500/40 px-3 py-2 text-xs font-mono text-error-600">
        {typeof event.content === 'string' ? event.content : JSON.stringify(event.content)}
      </p>
    )
  }
  return null
}

/**
 * Um item = um turno (a pergunta e a resposta dela). Endereçar por `turnId`, e não por
 * índice do array, é o que garante que os eventos caiam sempre na bolha certa mesmo com
 * troca de conversa ou remontagem da tela.
 */
function turnsToMessages(turns = []) {
  return turns.map((t) => ({
    chave: t.id,
    turnId: t.id,
    prompt: t.prompt,
    autor: t.usuario_nome,
    events: t.events || [],
    status: t.status,
    error: t.error,
  }))
}

export default function GestaoIA() {
  const { data: tenant } = useTenant()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const sessaoDaUrl = params.get('sessao')

  const [sessionId, setSessionId] = useState(sessaoDaUrl)
  const [sessao, setSessao] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [activeTurnId, setActiveTurnId] = useState(null)
  const [error, setError] = useState(null)
  const [offline, setOffline] = useState(false)
  const [booting, setBooting] = useState(true)
  const endRef = useRef(null)
  const containerRef = useRef(null)
  // Enquanto o operador está "grudado" perto do fim, o autoscroll segue a resposta ao vivo.
  // Se ele arrasta para cima para reler algo, soltamos; ao voltar ao fim, gruda de novo.
  const grudadoNoFimRef = useRef(true)
  // Cada carregamento ganha um número. O polling só escreve na tela se o seu número ainda
  // for o corrente — é assim que trocar de conversa não deixa dois loops brigando.
  const execucaoRef = useRef(0)

  const invalidarLista = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['ai-sessions'] }),
    [queryClient],
  )

  // ---------------------------------------------------------------- polling

  const pollTurn = useCallback(
    async (turnId, startCursor, token) => {
      let cursor = startCursor
      let failures = 0
      setActiveTurnId(turnId)
      setBusy(true)
      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (execucaoRef.current !== token) return
          let turn
          try {
            turn = await aiEngineApi.getTurn(turnId, cursor)
            failures = 0
            setOffline(false)
          } catch (err) {
            // O turno segue rodando no servidor — só perdemos a visão dele.
            failures += 1
            setOffline(true)
            if (failures >= MAX_POLL_FAILURES) throw err
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
            continue
          }

          cursor = turn.cursor
          setMessages((prev) =>
            prev.map((m) =>
              m.turnId === turnId
                ? {
                    ...m,
                    events: turn.events?.length ? [...m.events, ...turn.events] : m.events,
                    status: turn.status,
                    error: turn.error,
                    parcial: turn.partial,
                  }
                : m,
            ),
          )

          if (turn.status !== 'running') {
            // A lista mostra "rodando" e a contagem de mensagens — atualiza junto.
            void invalidarLista()
            return
          }
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
        }
      } finally {
        if (execucaoRef.current === token) {
          setBusy(false)
          setActiveTurnId(null)
        }
      }
    },
    [invalidarLista],
  )

  // ---------------------------------------------------------------- qual conversa abrir

  // Sem `?sessao=` na URL, cai na conversa mais recente que ESTE operador enxerga (as dele
  // + as compartilhadas com ele). Nenhuma é criada sozinha: abrir conversa é ato explícito.
  useEffect(() => {
    if (sessaoDaUrl) {
      setSessionId(sessaoDaUrl)
      return
    }
    let vivo = true
    ;(async () => {
      try {
        const { sessions } = await aiEngineApi.listSessions()
        if (!vivo) return
        setSessionId(sessions?.[0]?.id ?? null)
        if (!sessions?.length) setBooting(false)
      } catch (err) {
        if (!vivo) return
        setError(err.message || 'Falha ao abrir a Gestão IA.')
        setBooting(false)
      }
    })()
    return () => {
      vivo = false
    }
  }, [sessaoDaUrl])

  // Carrega o histórico da conversa ativa e reata um turno que esteja rodando no servidor.
  useEffect(() => {
    if (!sessionId) return
    const token = ++execucaoRef.current
    setBooting(true)
    setError(null)
    ;(async () => {
      try {
        const detalhe = await aiEngineApi.sessionDetail(sessionId)
        if (execucaoRef.current !== token) return
        setSessao(detalhe)
        setMessages(turnsToMessages(detalhe.turns))
        setBooting(false)

        const ultimo = detalhe.turns?.[detalhe.turns.length - 1]
        if (ultimo?.status === 'running') {
          void pollTurn(ultimo.id, (ultimo.events || []).length, token)
        }
      } catch (err) {
        if (execucaoRef.current !== token) return
        setError(err.message || 'Falha ao abrir a conversa.')
        setBooting(false)
      }
    })()
  }, [sessionId, pollTurn])

  const aoRolar = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    // Margem de 80px cobre o arredondamento do scroll e a barra de digitação.
    grudadoNoFimRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }, [])

  useEffect(() => {
    if (grudadoNoFimRef.current) endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // ---------------------------------------------------------------- ações

  function selecionar(id) {
    if (id === sessionId) return
    // Não interrompe nada: o turno da conversa anterior segue rodando no servidor, e a
    // lista continua mostrando "rodando".
    execucaoRef.current += 1
    grudadoNoFimRef.current = true
    setBusy(false)
    setActiveTurnId(null)
    setMessages([])
    setSessao(null)
    setSessionId(id)
    setParams({ sessao: id }, { replace: true })
  }

  async function novaConversa() {
    setError(null)
    try {
      // Abrir outra conversa NÃO encerra a atual — ela continua na lista, com o histórico.
      // Nasce privada; compartilhar é decisão do dono, no menu da própria conversa.
      const criada = await aiEngineApi.createSession({ tenantName: tenant?.name })
      await invalidarLista()
      selecionar(criada.sessionId)
    } catch (err) {
      setError(err.message || 'Falha ao abrir nova conversa.')
    }
  }

  function aoArquivar(id) {
    if (id !== sessionId) return
    execucaoRef.current += 1
    setSessionId(null)
    setSessao(null)
    setMessages([])
    setParams({}, { replace: true })
  }

  async function handleCancel() {
    if (!activeTurnId) return
    try {
      await aiEngineApi.cancelTurn(activeTurnId)
    } catch (err) {
      setError(err.message || 'Falha ao cancelar.')
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const prompt = input.trim()
    if (!prompt || busy || !sessionId) return

    setInput('')
    setError(null)
    const token = execucaoRef.current
    const chave = `local-${Date.now()}`
    setBusy(true)
    setMessages((prev) => [
      ...prev,
      // Só otimismo local: quem carimba a autoria de verdade é o servidor, pelo token.
      // (o payload do login aparece nas duas caixas conforme a origem — ver getTenantId)
      { chave, turnId: null, prompt, autor: user?.name || user?.Name || null, events: [], status: 'running' },
    ])

    try {
      const { turnId } = await aiEngineApi.createTurn(sessionId, prompt)
      setMessages((prev) => prev.map((m) => (m.chave === chave ? { ...m, turnId } : m)))
      void invalidarLista()
      await pollTurn(turnId, 0, token)
    } catch (err) {
      const msg = err.message || 'Falha ao executar o turno.'
      setError(msg)
      setMessages((prev) =>
        prev.map((m) => (m.chave === chave ? { ...m, status: 'error', error: msg } : m)),
      )
      setBusy(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  // ---------------------------------------------------------------- render

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col">
      <div className="mb-3">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-theme-text">
          <Bot className="h-6 w-6 text-theme-primary" />
          Gestão IA
        </h1>
        <p className="mt-1 text-sm text-theme-text-muted">
          Agente com acesso à infraestrutura. O trabalho roda no servidor — pode fechar esta aba
          ou trocar de conversa.
          {tenant?.name && <span className="ml-2 font-medium">Tenant: {tenant.name}</span>}
        </p>
      </div>

      <div className="mb-3 flex items-start gap-2 rounded-theme-md border border-warning-500/40 px-3 py-2 text-xs text-warning-600">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Este agente roda como root no servidor de produção e pode alterar equipamentos de clientes.
          Alterações de código são commitadas no repositório e publicadas pelo GitHub Actions.
        </span>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden rounded-theme-md border border-theme-surface-border bg-theme-surface">
        <div className="w-72 shrink-0 border-r border-theme-border">
          <ListaSessoes
            sessaoAtivaId={sessionId}
            onSelecionar={selecionar}
            onNova={() => void novaConversa()}
            onArquivada={aoArquivar}
            criando={booting}
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          {offline && (
            <div className="flex items-center gap-2 border-b border-theme-border px-3 py-2 text-xs text-theme-text-muted">
              <WifiOff className="h-4 w-4 shrink-0" />
              Sem contato com o servidor — o turno continua rodando lá. Reconectando…
            </div>
          )}

          <div ref={containerRef} onScroll={aoRolar} className="flex-1 overflow-y-auto p-4">
            {booting && (
              <div className="flex items-center gap-2 text-sm text-theme-text-muted">
                <Loader2 className="h-4 w-4 animate-spin" />
                Carregando a conversa…
              </div>
            )}

            {!booting && !sessionId && (
              <p className="text-sm text-theme-text-muted">
                Selecione uma conversa à esquerda ou abra uma nova.
              </p>
            )}

            {!booting && sessionId && messages.length === 0 && (
              <p className="text-sm text-theme-text-muted">
                Pergunte sobre o estado da infraestrutura, peça um diagnóstico ou uma correção de
                código.
              </p>
            )}

            {messages.map((m) => (
              <div key={m.chave} className="mb-5">
                <div className="flex gap-3">
                  <User className="mt-1 h-4 w-4 shrink-0 text-theme-text-muted" />
                  <div className="min-w-0 flex-1">
                    {/* Numa conversa compartilhada quem escreveu cada pergunta importa. */}
                    {m.autor && (
                      <p className="text-[11px] font-medium uppercase tracking-wide text-theme-text-muted">
                        {m.autor}
                      </p>
                    )}
                    <p className="whitespace-pre-wrap font-medium text-theme-text">{m.prompt}</p>
                  </div>
                </div>

                <div className="mt-3 flex gap-3">
                  <Bot className="mt-1 h-4 w-4 shrink-0 text-theme-primary" />
                  <div className="min-w-0 flex-1">
                    {(m.events || []).map((event, j) => (
                      <EventBlock key={j} event={event} />
                    ))}
                    {m.parcial && (
                      <p className="whitespace-pre-wrap leading-relaxed text-theme-text">
                        {m.parcial}
                        <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-theme-text-muted align-text-bottom" />
                      </p>
                    )}
                    {m.status === 'running' && !m.parcial && (
                      <div className="flex items-center gap-2 text-sm text-theme-text-muted">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Trabalhando…
                      </div>
                    )}
                    {m.status === 'cancelled' && (
                      <p className="text-sm text-theme-text-muted">Cancelado.</p>
                    )}
                    {m.status === 'interrupted' && (
                      <p className="text-sm text-warning-600">
                        {m.error || 'O serviço reiniciou durante este turno.'}
                      </p>
                    )}
                    {m.status === 'error' && (
                      <p className="text-sm text-error-600">{m.error || 'Falha no turno.'}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {error && <p className="px-4 pb-1 text-sm text-error-600">{error}</p>}

          <form onSubmit={handleSubmit} className="flex items-end gap-2 border-t border-theme-border p-3">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              disabled={busy || booting || !sessionId}
              placeholder="Ex.: por que o vpnserver reiniciou hoje? Olhe o journal e me diga a causa."
              className="flex-1 resize-none rounded-theme-md border border-theme-border bg-theme-bg px-3 py-2 text-sm text-theme-text placeholder:text-theme-text-muted focus:border-theme-primary focus:outline-none disabled:opacity-50"
            />
            {busy ? (
              <button
                type="button"
                onClick={handleCancel}
                className="flex h-10 items-center gap-2 rounded-theme-md border border-theme-border px-4 text-sm font-medium text-theme-text hover:bg-theme-bg"
              >
                <Square className="h-4 w-4" />
                Cancelar
              </button>
            ) : (
              <button
                type="submit"
                disabled={booting || !input.trim() || !sessionId}
                className={clsx(
                  'flex h-10 items-center gap-2 rounded-theme-md bg-theme-primary px-4 text-sm font-medium text-white',
                  'disabled:opacity-50',
                )}
              >
                <Send className="h-4 w-4" />
                Enviar
              </button>
            )}
          </form>
        </div>
      </div>

      {sessao && (
        <p className="mt-2 text-[11px] text-theme-text-muted">
          Conversa de {sessao.usuario_nome || 'autor desconhecido'}
          {sessao.souDono ? ' (sua)' : ''} — {sessao.visibilidade || 'privada'}.
        </p>
      )}
    </div>
  )
}
